import { NavigationButton, Section } from '@rumblefish/ui/Rumblefish23Theme';
import React from 'react';

import type { ICaseStudyCard } from './JoinThem.types';
import {
  CardAccentBar,
  CardArrow,
  CardBg,
  CardContent,
  CardHeadline,
  CardLabel,
  CardLink,
  CardLogo,
  CardLogoPlaceholder,
  CardScrim,
  CardsGrid,
  CardTop,
  CardWrap,
  CtaStack,
  StyledStack,
  TextStack,
  Title,
} from './styles';

const ArrowSvgIcon = (): React.ReactElement => (
  <CardArrow
    className="CaseStudyCard-arrow"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M51.9733 32.0269L12 32.0269M35.9627 16.0615L52.0053 32.0002L35.9627 47.9389"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </CardArrow>
);

const CaseStudyCard = ({
  card,
  index,
}: {
  card: ICaseStudyCard;
  index: number;
}): React.ReactElement => {
  const imgUrl = card.image;
  const logoUrl = card.logo;

  return (
    <CardWrap accentindex={index}>
      <CardLink to={card.link} label={card.title} className="CaseStudyCard-link">
        <CardBg className="CaseStudyCard-bg" imgurl={imgUrl} />
        <CardScrim />
        <CardContent>
          <CardTop>
            {logoUrl ? (
              <CardLogo
                src={logoUrl}
                alt={card.title}
                logoislight={card.logoIsLight}
              />
            ) : (
              <CardLogoPlaceholder variant="display_xxxs">
                {card.title}
              </CardLogoPlaceholder>
            )}
            <ArrowSvgIcon />
          </CardTop>
          <CardHeadline variant="display_xxs">{card.desc}</CardHeadline>
          <CardLabel variant="strong_100">{card.title}</CardLabel>
          <CardAccentBar accentindex={index} aria-hidden="true" />
        </CardContent>
      </CardLink>
    </CardWrap>
  );
};

export const JoinThem = (): React.ReactElement => {
  const jointThemData: ICaseStudyCard[] = [
    {
      title: 'Tari',
      desc: "Bridging Tari's native token into the Ethereum DeFi ecosystem",
      link: '/case-studies/Bridging-Tari-to-Ethereum/',
      image: 'https://strapi-uploads.rumblefish.dev/Tables_768a98f152.png',
      logo: 'https://strapi-uploads.rumblefish.dev/tari_e420f69a7d.jpg',
    },
    {
      title: 'EVM Debugger',
      desc: 'A smarter way to debug EVM smart contracts',
      link: '/case-studies/evm-debugger/',
      image: 'https://strapi-uploads.rumblefish.dev/Tables_1_11000f8b12.png',
      logo: 'https://strapi-uploads.rumblefish.dev/EVM_debugger_logo_a8d54a83c4.png',
      logoIsLight: true,
    },
    {
      title: 'Original Works',
      desc: 'A decentralized, ZK-powered IP registry for music rights',
      link: '/case-studies/decentralized-ddex-registry-original-works/',
      image:
        'https://strapi-uploads.rumblefish.dev/1728653346_ow_734f23544e_58b14cf0c2.avif',
      logo: 'https://strapi-uploads.rumblefish.dev/Original_Works_Icon_0212cd6f89.png',
    },
    {
      title: 'Panenka',
      desc: 'An AI-powered football manager game',
      link: '/case-studies/panenka/',
      image:
        'https://strapi-uploads.rumblefish.dev/panenka_Hero_Image_Fixed_d8b696cafe.png',
      logo: 'https://strapi-uploads.rumblefish.dev/panenka_Logo_1b7fd781db.png',
      logoIsLight: true,
    },
  ];

  return (
    <Section width="normal">
      <StyledStack>
        <TextStack>
          <Title variant="h2">
            We're trusted by global innovators and leaders. Join them!
          </Title>
        </TextStack>
        <CardsGrid>
          {jointThemData.map((card, index) => (
            <CaseStudyCard card={card} index={index} key={card.title} />
          ))}
        </CardsGrid>
        <CtaStack>
          <NavigationButton
            target="/case-studies"
            buttonText="View all case studies"
            color="raspberry"
            ariaLabel="View all case studies"
          />
        </CtaStack>
      </StyledStack>
    </Section>
  );
};
